import {toast} from '@wandb/common/components/elements/Toast';
import WandbLoader from '@wandb/common/components/WandbLoader';
import {
  connectionToNodes,
  opArtifactId,
  opArtifactName,
  opArtifactProject,
  opArtifactRawTags,
  opArtifactType,
  opArtifactTypeName,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave-ui/cg';
import {useValue} from '@wandb/weave-ui/cgreact';
import * as Panel2 from '@wandb/weave-ui/components/Panel2/panel';
import * as React from 'react';
import styled from 'styled-components';

import * as Generated from '../../../generated/graphql';
import * as GQLTypes from '../../../types/graphql';
import {getErrMsgFromError, propagateErrorsContext} from '../../../util/errors';
import {
  AddTagAssignmentButton,
  createAttributesString,
  mapGQLTags,
  matchTag,
  ModifiedTag,
  parseTag,
  TagAssignment,
  TagAttributes,
} from '../../TagAssignment';

const inputType = 'artifact' as const;
export type ConfigType = {};

export const ArtifactTagList = styled.div<{wrapList?: boolean}>`
  display: flex;
  ${props => (props.wrapList ? `flex-wrap: wrap; row-gap: 5px;` : ``)}
  overflow:auto;
`;

type TagListInnerProps = {
  loading: boolean;
  refresh: () => void;
  entityName: string;
  projectName: string;
  artifactCollectionName: string;
  currentTags: ModifiedTag[];
  allTags: ModifiedTag[];
};

type TagListProps = {
  showButton: boolean;
  showColor: boolean;
  tagSize: 'small' | 'medium' | 'large';
  wrap?: boolean;
  showLoader?: boolean;
};

const PanelArtifactTagList: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & TagListProps
> = props => {
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        artifactTypeName: opArtifactTypeName({
          artifactType: opArtifactType({artifact: artifactNode}),
        }),
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        collectionName: opArtifactName({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
        collectionRawTags: opArtifactRawTags({
          artifact: artifactNode,
        }),
      } as any);
    }, [artifactNode])
  );

  const currentTagsRaw: Array<{
    id: string;
    name: string;
    tagCategoryName: string;
    attributes: string;
  }> = React.useMemo(
    () => overviewValue.result?.collectionRawTags ?? [],
    [overviewValue.result?.collectionRawTags]
  );

  const currentTags = mapGQLTags(currentTagsRaw);

  const {
    data: entityData,
    loading: entityLoading,
    error: entityError,
  } = Generated.useEntityTagsQuery({
    variables: {
      entityName: overviewValue.result?.entityName,
    },
    skip: overviewValue.result == null,
  });

  const entity = entityData?.entity;
  const allTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    return mapGQLTags(rawTags);
  }, [entity]);

  if (overviewValue.loading || overviewValue.result == null || entityLoading) {
    if (props.showLoader) {
      return <WandbLoader name="artifact-collection-tag-list" />;
    } else {
      return <></>;
    }
  }

  // We don't want to display the error message in case of the
  // entity query failing as they only list out existing tags on the collection
  if (entityError) {
    const errMsg = getErrMsgFromError(entityError);
    console.error(`Error retrieving entity tags; Msg: ${errMsg}`);
  }

  return (
    <PanelArtifactTagListInner
      {...props}
      loading={overviewValue.loading}
      refresh={overviewValue.refresh}
      entityName={overviewValue.result.entityName}
      projectName={overviewValue.result.projectName}
      artifactCollectionName={overviewValue.result.collectionName}
      currentTags={currentTags ?? []}
      allTags={allTags ?? []}
    />
  );
};

export const PanelArtifactTagListInner: React.FC<
  TagListInnerProps & TagListProps
> = props => {
  const [addTagAssignment] =
    Generated.useCreateArtifactCollectionTagAssignmentMutation({
      context: propagateErrorsContext(),
    });
  const [removeTagAssignment] =
    Generated.useDeleteArtifactCollectionTagAssignmentMutation({
      context: propagateErrorsContext(),
    });

  const createTagAssignment = React.useCallback(
    async (tagInput: GQLTypes.Tag) => {
      try {
        const tagStr = tagInput.name;
        if (props.currentTags.find((t: ModifiedTag) => matchTag(t, tagStr))) {
          return Promise.resolve();
        }

        // Parse input string into tag and category
        const [tagCategoryName, tagName] = parseTag(tagStr);

        // Stringify Attributes
        const tagAttributes: TagAttributes = {
          color: tagInput.colorIndex,
          icon: tagInput.icon,
        };
        const attributesStr = createAttributesString(tagAttributes);
        await addTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tagCategoryName,
            tagName,
            attributes: attributesStr,
          },
        });
        props.refresh();
      } catch (err) {
        console.error(err);
        toast(
          'Something went wrong when trying to create this tag. Please check for special characters.'
        );
      }
      return;
    },
    [addTagAssignment, props]
  );

  const deleteTagAssignment = React.useCallback(
    async (tag: ModifiedTag) => {
      try {
        await removeTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tagCategoryName: tag.tagCategoryName,
            tagName: tag.name,
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this tag.');
      }
      return;
    },
    [removeTagAssignment, props]
  );

  return (
    <ArtifactTagList wrapList={props.wrap}>
      {props.currentTags.map(tag => {
        return (
          <TagAssignment
            key={tag.tagCategoryName + tag.name}
            tag={tag}
            showColor={props.showColor}
            size={props.tagSize}
            canDelete={props.showButton}
            onDelete={() => deleteTagAssignment(tag)}
          />
        );
      })}
      {props.showButton && (
        <AddTagAssignmentButton
          assignedTags={props.currentTags}
          availableTags={props.allTags}
          onCreate={createTagAssignment}
        />
      )}
    </ArtifactTagList>
  );
};

export default PanelArtifactTagList;
